import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { Search, AlertCircle, Plus } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { OpStatusBadge } from '@/components/operations/OpStatusBadge';
import { OperationFormDialog } from '@/components/operations/OperationFormDialog';
import {
  useCompanyOpTypes,
  useCreateOperationAnyField,
} from '@/components/operations/operationsApi';
import { Button } from '@/components/ui/button';
import { PERM, useCan } from '@/lib/auth/usePermissions';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  VirtualTable,
  type VirtualColumn,
  type SortState,
} from '@/components/ui/virtual-table';
import { api, ApiError } from '@/lib/api';
import { formatRuDate } from '@/lib/dateFmt';
import { fetchScopeAvailable, invalidateScopeAvailable } from '@/lib/http/sharedQueries';
import { useScopeChanged, type ScopeChangedDetail } from '@/lib/scopeEvent';

/**
 * /operations — бесконечный список (AK-348).
 *
 * Постраничная навигация заменена на VirtualTable: порции по 100 доезжают
 * при прокрутке (offset-пагинация на бэке), фильтры/поиск выполняются на
 * сервере. Смена любого фильтра сбрасывает скролл и перезапрашивает набор.
 *
 * AK-866: область из шапки (хозяйство / кластер) — фильтр списка, как у карты
 * и «Полей». Смена области в шапке перезапрашивает список (`useScopeChanged`);
 * своего контрола области на экране нет — единственный источник здесь панель.
 */

type PanelScope = Pick<ScopeChangedDetail, 'scope_type' | 'scope_id'>;

interface Operation {
  id: string;
  field_id: string;
  field_name?: string | null;
  name: string;
  op_type_local_name?: string | null;
  lifecycle_state: string;
  was_rescheduled: boolean;
  display_status?: string | null;
  planned_start_date?: string | null;
  actual_start_date?: string | null;
  planned_area_ha?: number | null;
  actual_area_ha?: number | null;
}

interface OperationListResponse {
  total: number;
  items: Operation[];
}

interface CompanyOpType {
  id: string;
  local_name: string;
}

const DISPLAY_STATUSES = [
  { value: 'planned', label: 'Запланирована' },
  { value: 'overdue', label: 'Просрочена' },
  { value: 'in_progress', label: 'Выполняется' },
  { value: 'done_full', label: 'Выполнена полностью' },
  { value: 'done_partial', label: 'Выполнена частично' },
  { value: 'cancelled', label: 'Отменена' },
];

const ALL_VALUE = '__all__';
const PAGE = 100;

function dateOf(o: Operation): string | null {
  return o.actual_start_date ?? o.planned_start_date ?? null;
}

export function OperationsListPage() {
  const navigate = useNavigate();
  // AK-526: до сих пор создать операцию из веба было негде вообще.
  const canCreate = useCan(PERM.operationWrite, 'c');
  const [createOpen, setCreateOpen] = useState(false);
  const { data: opTypes } = useCompanyOpTypes();
  const createOperation = useCreateOperationAnyField();
  const [items, setItems] = useState<Operation[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true); // initial load
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [cotList, setCotList] = useState<CompanyOpType[]>([]);
  const [filterCot, setFilterCot] = useState<string | undefined>();
  const [filterStatus, setFilterStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [sort, setSort] = useState<SortState | null>(null);

  // Область из шапки. До ответа `/scope/available` список не запрашиваем:
  // иначе первым уходит самый дорогой запрос «все операции кабинета», который
  // через мгновение переигрывается под хозяйство шапки.
  const [panelScope, setPanelScope] = useState<PanelScope | null>(null);
  const [scopeReady, setScopeReady] = useState(false);
  useEffect(() => {
    let alive = true;
    fetchScopeAvailable<{ current?: PanelScope | null }>()
      .then((d) => {
        if (alive) setPanelScope(d?.current ?? null);
      })
      .catch(() => {
        /* без области — весь доступный список, граница всё равно на сервере */
      })
      .finally(() => {
        if (alive) setScopeReady(true);
      });
    return () => {
      alive = false;
    };
  }, []);
  // Событие несёт новую область сразу — не ждём перечитки `/scope/available`.
  // Её кэш при этом протух: `current` в нём прежний.
  useScopeChanged((detail) => {
    invalidateScopeAvailable();
    setPanelScope(detail);
    setScopeReady(true);
  });
  const scopeFarm =
    panelScope?.scope_type === 'farm' ? (panelScope.scope_id ?? undefined) : undefined;
  const scopeCluster =
    panelScope?.scope_type === 'cluster' ? (panelScope.scope_id ?? undefined) : undefined;

  // guard against out-of-order responses on rapid filter changes
  const reqSeq = useRef(0);
  const offsetRef = useRef(0);

  useEffect(() => {
    api
      .get<CompanyOpType[]>('/api/v1/company-operation-types')
      .then(setCotList)
      .catch(() => {
        /* tolerate */
      });
  }, []);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  // filter+sort signature — changing it resets the list + scroll to topmost
  const resetKey = useMemo(
    () =>
      JSON.stringify([
        scopeFarm ?? '',
        scopeCluster ?? '',
        filterCot ?? '',
        filterStatus ?? '',
        debouncedQ,
        sort?.field ?? '',
        sort?.order ?? '',
      ]),
    [scopeFarm, scopeCluster, filterCot, filterStatus, debouncedQ, sort],
  );

  const buildParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (scopeFarm) params.set('farm_id', scopeFarm);
      if (scopeCluster) params.set('cluster_id', scopeCluster);
      if (filterCot) params.set('company_op_type_id', filterCot);
      if (filterStatus) params.set('display_status', filterStatus);
      if (debouncedQ) params.set('search', debouncedQ);
      if (sort) {
        params.set('sort', sort.field);
        params.set('order', sort.order);
      }
      params.set('limit', String(PAGE));
      params.set('offset', String(offset));
      return params;
    },
    [scopeFarm, scopeCluster, filterCot, filterStatus, debouncedQ, sort],
  );

  // Load first page (fresh filter set). Bumps seq → in-flight loadMore stale.
  const loadFirst = useCallback(() => {
    const seq = ++reqSeq.current;
    offsetRef.current = 0;
    setLoading(true);
    setError(null);
    api
      .get<OperationListResponse>(`/api/v1/operations?${buildParams(0)}`)
      .then((resp) => {
        if (seq !== reqSeq.current) return; // stale
        setItems(resp.items);
        setTotal(resp.total);
        offsetRef.current = resp.items.length;
      })
      .catch((e) => {
        if (seq !== reqSeq.current) return;
        setError(e instanceof ApiError ? e.detail : 'Не удалось загрузить операции');
        setItems([]);
        setTotal(0);
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoading(false);
      });
  }, [buildParams]);

  // (re)load first page whenever the filter signature changes
  useEffect(() => {
    if (!scopeReady) return;
    loadFirst();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey, scopeReady]);

  const loadMore = useCallback(() => {
    if (loadingMore || loading) return;
    if (offsetRef.current >= total) return;
    const seq = reqSeq.current;
    const offset = offsetRef.current;
    setLoadingMore(true);
    setError(null);
    api
      .get<OperationListResponse>(`/api/v1/operations?${buildParams(offset)}`)
      .then((resp) => {
        if (seq !== reqSeq.current) return; // stale (filters changed)
        setItems((prev) => [...prev, ...resp.items]);
        setTotal(resp.total);
        offsetRef.current = offset + resp.items.length;
      })
      .catch((e) => {
        if (seq !== reqSeq.current) return;
        setError(e instanceof ApiError ? e.detail : 'Не удалось догрузить');
      })
      .finally(() => {
        if (seq === reqSeq.current) setLoadingMore(false);
      });
  }, [loadingMore, loading, total, buildParams]);

  const retry = useCallback(() => {
    setError(null);
    if (items.length === 0) loadFirst();
    else loadMore();
  }, [items.length, loadFirst, loadMore]);

  const columns: VirtualColumn<Operation>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Операция',
        sortKey: 'name',
        width: 'minmax(200px, 1.4fr)',
        cell: (r) => (
          <div className="flex min-w-0 flex-col">
            <Link
              to={`/operations/${r.id}` as string}
              onClick={(e) => e.stopPropagation()}
              className="truncate font-medium text-foreground hover:text-primary"
            >
              {r.name}
            </Link>
            {r.op_type_local_name &&
              r.op_type_local_name.trim() !== r.name.trim() && (
                <span className="truncate text-[11px] text-muted-foreground">
                  {r.op_type_local_name}
                </span>
              )}
          </div>
        ),
      },
      {
        key: 'field',
        header: 'Поле',
        sortKey: 'field',
        width: 'minmax(140px, 1fr)',
        cell: (r) =>
          r.field_name ? (
            <Link
              to={`/fields/${r.field_id}` as string}
              onClick={(e) => e.stopPropagation()}
              className="truncate text-primary hover:underline"
            >
              {r.field_name}
            </Link>
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'status',
        header: 'Статус',
        sortKey: 'status',
        width: '220px',
        cell: (r) => (
          <OpStatusBadge status={r.display_status} rescheduled={r.was_rescheduled} />
        ),
      },
      {
        key: 'date',
        header: 'Дата',
        sortKey: 'date',
        width: '130px',
        cell: (r) =>
          dateOf(r) ? (
            formatRuDate(dateOf(r))
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
      },
      {
        key: 'area',
        header: 'Площадь план/факт, га',
        sortKey: 'actual_area_ha',
        width: '170px',
        align: 'right',
        cell: (r) => (
          <span className="text-xs">
            {r.planned_area_ha?.toFixed(1) ?? '—'} /{' '}
            <strong>{r.actual_area_ha?.toFixed(1) ?? '—'}</strong>
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <AppShell>
      {/* AK-461 (founder 2026-07-27): без капа ширины — таблица тянется по экрану. */}
      <div className="flex h-full w-full flex-col gap-4">
        <PageHeader
          title="Тех. операции"
          showBack={false}
          actions={
            canCreate ? (
              <Button size="sm" onClick={() => setCreateOpen(true)}>
                <Plus className="h-4 w-4" />
                Добавить операцию
              </Button>
            ) : undefined
          }
        />

        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative w-full sm:w-[300px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Поиск по названию/полю"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="w-full sm:w-[220px]">
            <Select
              value={filterCot ?? ALL_VALUE}
              onValueChange={(v) => setFilterCot(v === ALL_VALUE ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Тип операции" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все типы</SelectItem>
                {cotList.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.local_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-full sm:w-[220px]">
            <Select
              value={filterStatus ?? ALL_VALUE}
              onValueChange={(v) => setFilterStatus(v === ALL_VALUE ? undefined : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Статус" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_VALUE}>Все статусы</SelectItem>
                {DISPLAY_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && items.length > 0 && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <div className="min-h-0 flex-1">
          <VirtualTable
            columns={columns}
            rows={items}
            rowKey={(r) => r.id}
            total={total}
            hasMore={items.length < total}
            loading={loading}
            loadingMore={loadingMore}
            error={error}
            onLoadMore={loadMore}
            onRetry={retry}
            onRowClick={(r) => navigate({ to: `/operations/${r.id}` as string })}
            unitLabel="операций"
            emptyText="Нет операций"
            restoreKey="operations"
            resetKey={resetKey}
            sort={sort}
            onSortChange={setSort}
            height="100%"
          />
        </div>
      </div>

      {/* Создание из общего списка: поля в контексте нет, поэтому форма
          показывает поиск по полям. После сохранения — сразу в карточку
          операции, чтобы можно было завести работы, а не искать её в списке. */}
      <OperationFormDialog
        open={createOpen}
        operation={null}
        opTypes={opTypes ?? []}
        saving={createOperation.isPending}
        error={createOperation.error?.message ?? null}
        onSubmit={(body) => {
          if (!body.field_id) return; // форма не отдаёт submit без поля
          createOperation.mutate(
            { ...body, field_id: body.field_id },
            {
              onSuccess: (op) => {
                setCreateOpen(false);
                void navigate({ to: `/operations/${op.id}` as string });
              },
            },
          );
        }}
        onClose={() => setCreateOpen(false)}
      />
    </AppShell>
  );
}

export default OperationsListPage;
